import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ListOrdered, CalendarDays, MapPin, Clock } from "lucide-react";
import api from "@/lib/api";
import FranchiseEnquiry from "@/components/FranchiseEnquiry";

const EASE = [0.22, 1, 0.36, 1];

function StatusChip({ status }) {
  if (status === "live")
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-league-red text-white text-[10px] font-bold uppercase tracking-widest">
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
      </span>
    );
  if (status === "completed")
    return <span className="px-3 py-1 rounded-full bg-league-ink/10 text-league-ink/60 text-[10px] font-bold uppercase tracking-widest">Completed</span>;
  return <span className="px-3 py-1 rounded-full bg-league-blue text-white text-[10px] font-bold uppercase tracking-widest">Upcoming</span>;
}

function fmtDate(d) {
  try { return format(new Date(d), "EEE, dd MMM yyyy"); } catch { return d; }
}

export default function Season() {
  const [fixtures, setFixtures] = useState([]);

  useEffect(() => {
    api.get("/fixtures").then((r) => setFixtures(r.data)).catch(() => {});
  }, []);

  return (
    <main className="pt-28 bg-league-mist min-h-screen" data-testid="season-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-14">
        <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue flex items-center gap-2">
          <ListOrdered size={14} /> 19–21 February 2027 · Umra Cricket Ground, Surat
        </span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-league-ink tracking-tighter mt-3">
          SEASON <span className="text-outline">6.0</span>
        </h1>
      </div>

      <section className="py-20 bg-white" data-testid="fixtures-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-display text-4xl sm:text-5xl text-league-ink tracking-tighter mb-10">FIXTURES</h2>
          {fixtures.length === 0 && (
            <div className="border-2 border-dashed border-league-blue/30 rounded-2xl p-12 text-center" data-testid="fixtures-empty">
              <CalendarDays size={28} className="mx-auto text-league-blue mb-4" />
              <p className="font-oswald text-xl font-semibold text-league-ink">Fixtures dropping soon</p>
              <p className="text-muted-foreground text-sm mt-2">The February 2027 schedule is being finalised — check back shortly.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fixtures.map((f, i) => (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08, ease: EASE }}
                className="bg-league-mist border border-black/5 rounded-xl p-6 hover:-translate-y-1 hover:shadow-[0_18px_36px_-14px_rgba(0,74,173,0.25)] transition-[transform,box-shadow] duration-300"
                data-testid={`fixture-card-${f.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <StatusChip status={f.status} />
                  <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <CalendarDays size={13} /> {fmtDate(f.date)}
                  </span>
                </div>
                <p className="font-oswald text-xl sm:text-2xl font-semibold text-league-ink">
                  {f.team_a} <span className="text-league-red">vs</span> {f.team_b}
                </p>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                  {f.time && <span className="flex items-center gap-1.5"><Clock size={13} /> {f.time}</span>}
                  {f.venue && <span className="flex items-center gap-1.5"><MapPin size={13} /> {f.venue}</span>}
                </div>
                {f.result && <p className="mt-3 text-sm font-semibold text-league-blue" data-testid={`fixture-result-${f.id}`}>{f.result}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 bg-white" data-testid="season-enquiry-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FranchiseEnquiry dark={false} />
        </div>
      </section>
    </main>
  );
}
